import { Plus, Trash2 } from 'lucide-react'
import type { LendingPartner } from '@/types'

export const BLANK_PARTNER: LendingPartner = { legalName: '', kind: 'nbfc', rbiCor: '', grievanceUrl: '' }

/** The rows that are filled in (an empty row while typing is never saved). */
export const filledPartners = (rows: LendingPartner[]) =>
  rows.filter((r) => r.legalName.trim()).map((r) => ({ legalName: r.legalName.trim(), kind: r.kind, rbiCor: r.kind === 'bank' ? '' : r.rbiCor.trim(), grievanceUrl: r.grievanceUrl.trim() }))

/**
 * Who actually lends (PF-1): an LSP's NBFC / bank partners, or the bank an NBFC co-lends with. One row each; the first
 * is named on the card ("loans by <first> and partner banks / NBFCs").
 */
export function LendingPartnersEditor({ value, onChange, idPrefix = 'lp' }: { value: LendingPartner[]; onChange: (rows: LendingPartner[]) => void; idPrefix?: string }) {
  const rows = value.length ? value : [BLANK_PARTNER]
  const set = (i: number, patch: Partial<LendingPartner>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-1 gap-2 rounded border border-slate-200 p-2 sm:grid-cols-[1fr_110px_130px_auto] dark:border-slate-700" data-testid="lending-partner-row">
          <div>
            <label className="label" htmlFor={`${idPrefix}-name-${i}`}>{i === 0 ? 'Lender (legal name, named on the card)' : 'Lender (legal name)'}</label>
            <input id={`${idPrefix}-name-${i}`} className="input" value={r.legalName} onChange={(e) => set(i, { legalName: e.target.value })} placeholder="Whizdm Finance Private Limited" />
          </div>
          <div>
            <label className="label" htmlFor={`${idPrefix}-kind-${i}`}>Type</label>
            <select id={`${idPrefix}-kind-${i}`} className="input" value={r.kind} onChange={(e) => set(i, { kind: e.target.value as LendingPartner['kind'] })}>
              <option value="nbfc">NBFC</option><option value="bank">Bank</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor={`${idPrefix}-cor-${i}`}>CoR {r.kind === 'bank' ? '(none for a bank)' : ''}</label>
            <input id={`${idPrefix}-cor-${i}`} className="input" value={r.kind === 'bank' ? '' : r.rbiCor} disabled={r.kind === 'bank'} onChange={(e) => set(i, { rbiCor: e.target.value })} placeholder="N-13.02234" />
          </div>
          <div className="flex items-end">
            <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove lender ${i + 1}`} disabled={rows.length === 1 && !r.legalName} onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 size={13} /></button>
          </div>
          <div className="sm:col-span-4">
            <label className="label" htmlFor={`${idPrefix}-gr-${i}`}>The lender’s grievance page (optional)</label>
            <input id={`${idPrefix}-gr-${i}`} className="input" value={r.grievanceUrl} onChange={(e) => set(i, { grievanceUrl: e.target.value })} placeholder="https://…/grievance" />
          </div>
        </div>
      ))}
      {rows.length < 20 && <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange([...rows, BLANK_PARTNER])}><Plus size={12} /> Add a lender</button>}
    </div>
  )
}
